/* eslint-disable no-new */
import $ from 'jquery';
import _ from 'underscore';
import { RSAAdCustomizerAnalyzer } from '@bingads-webui-campaign/ad-customizers-next';
import { AdCustomizerController } from './controller';
import { AdCustomizerFeedAnalyzer } from './feeds/analyzer';
import { AdCustomizerKeywordAnalyzer } from './keyword/analyzer';
import { AdCustomizerCountdownAnalyzer } from './countdown/analyzer';
import { AdCustomizerIffAnalyzer } from './iff/analyzer';
import { AdCustomizerLocationAnalyzer } from './location/analyzer';

// These singletons orchestrate the controllers and analyzers for each ad type
// that supports Ad Customizers. This is needed to allow certain ad types to use a subset of the
// available Ad Customizers (specifically the analyzers).
const instances = {
  ExpandedTextAd: {
    controller: null,
    initialized: false,
  },
  DynamicSearchAd: {
    controller: null,
    initialized: false,
  },
  ResponsiveSearchAd: {
    controller: null,
    initialized: false,
  },
  ResponsiveAd: {
    controller: null,
    initialized: false,
  },
};

const excludedAdCustomizerFields = {
  ResponsiveSearchAd: {
    countdown: ['Path1', 'Path2', 'ParsedPath1', 'ParsedPath2'],
  },
};

function getExcludedAdCustomizerFields(adType, customizer) {
  return excludedAdCustomizerFields[adType][customizer];
}

function checkAdType(adType) {
  if (!_.has(instances, adType)) {
    throw new Error(`Ad customizer for ad type '${adType}' may not be supported.`);
  }
}

function initialize(deps, adType = 'ExpandedTextAd') {
  checkAdType(adType);

  if (!instances[adType].initialized) {
    instances[adType].controller = new AdCustomizerController();
    const { config } = deps;
    const permissions = config.get('Permissions') || {};
    const {
      IsAdCustomizerEnabled, IsKeywordDefaultEnabled, IsIfFunctionMilestone1Enabled,
      IsIffCountdownForDSAEnabled, IsRSACountdownsPopupEnabled,
      IsRSALocationInsertionPopupEnabled, IsRSAAdCustomizerEnabled,
      IsMMACountdownInsertionEnabled, IsMMAKeywordInsertionEnabled, IsMMALocationInsertionEnabled,
      IsMMAAdCustomizerInsertionEnabled,
    } = permissions;

    const { controller } = instances[adType];
    if (IsIffCountdownForDSAEnabled && adType === 'DynamicSearchAd') {
      new AdCustomizerCountdownAnalyzer({ deps, controller });
      if (IsIfFunctionMilestone1Enabled) {
        new AdCustomizerIffAnalyzer({ deps, controller });
      }
    } else if (adType === 'ResponsiveSearchAd') {
      if (IsRSACountdownsPopupEnabled) {
        new AdCustomizerCountdownAnalyzer({
          deps,
          controller,
          excludedFields: getExcludedAdCustomizerFields(adType, 'countdown'),
        });
      }
      new AdCustomizerKeywordAnalyzer({ deps, controller });
      if (IsRSALocationInsertionPopupEnabled) {
        new AdCustomizerLocationAnalyzer({ deps, controller });
      }
      if (IsRSAAdCustomizerEnabled) {
        new RSAAdCustomizerAnalyzer({ deps, controller });
      }
    } else if (adType === 'ResponsiveAd') {
      if (IsMMACountdownInsertionEnabled) {
        new AdCustomizerCountdownAnalyzer({ deps, controller });
      }
      if (IsMMAKeywordInsertionEnabled) {
        new AdCustomizerKeywordAnalyzer({ deps, controller });
      }
      if (IsMMALocationInsertionEnabled) {
        new AdCustomizerLocationAnalyzer({ deps, controller });
      }
      if (IsMMAAdCustomizerInsertionEnabled) {
        new RSAAdCustomizerAnalyzer({ deps, controller });
      }
    } else {
      new AdCustomizerCountdownAnalyzer({ deps, controller });
      if (IsAdCustomizerEnabled) {
        new AdCustomizerFeedAnalyzer({ deps, controller });
      }
      if (IsKeywordDefaultEnabled) {
        new AdCustomizerKeywordAnalyzer({ deps, controller });
      }
      if (IsIfFunctionMilestone1Enabled) {
        new AdCustomizerIffAnalyzer({ deps, controller });
      }
    }
    instances[adType].initialized = true;
  } else if (deps.scope) {
    // update scope if already initialized
    const { controller: { analyzers } } = instances[adType];
    _.each(analyzers, ({ analyzer }) => {
      // eslint-disable-next-line no-param-reassign
      analyzer.scope = deps.scope;
    });
  }
}

function getController(deps, adType) {
  checkAdType(adType);

  let controller;
  if (adType === 'DynamicSearchAd') {
    initialize(deps, 'DynamicSearchAd');
    ({ controller } = instances.DynamicSearchAd);
  } else if (adType === 'ExpandedTextAd') {
    initialize(deps, 'ExpandedTextAd');
    ({ controller } = instances.ExpandedTextAd);
  } else if (adType === 'ResponsiveSearchAd') {
    initialize(deps, 'ResponsiveSearchAd');
    ({ controller } = instances.ResponsiveSearchAd);
  } else if (adType === 'ResponsiveAd') {
    initialize(deps, 'ResponsiveAd');
    ({ controller } = instances.ResponsiveAd);
  }

  return controller;
}

function testResetState(adType = 'ExpandedTextAd') {
  instances[adType].initialized = false;
  instances[adType].controller = null;
}

/*
 * Attaches focus, input event listeners to ad text fields
 */
function attachAdCustomizersHelpers({
  container = $('body'), // to attach popups to
  element,
  updateAction,
  remainingCountCallBack,
  deps,
  adType = 'ExpandedTextAd',
  field = '',
}) {
  const { instrumentation } = deps;
  const controller = getController(deps, adType);

  // add the class so that click in the input will not trigger picker to be hide
  element.addClass('ac-helper-area');

  element.on('input keyup focus click', (event) => {
    const { type, key, target } = event;
    const {
      isSelectedByUser, analyzerName, selectionStart, selectionEnd,
    } = target;
    target.isSelectedByUser = false;
    target.analyzerName = undefined;

    // Ignore if any text is selected, we are only interested in the cursor
    if (selectionStart !== selectionEnd) {
      return;
    }

    // Ignore escape key up since it shouldn't prompt any helper
    if (type === 'keyup' && key === 'Escape') {
      return;
    }

    controller.onInputUpdate({
      remainingCountCallBack,
      event,
      isSelectedByUser,
      analyzerName,
      updateAction,
      instrumentation,
      container, // to attach popups to
      adType,
      field,
    });
  });
}

// Calculates how much space ad customizer adds to the ad text field
function getCharCountExcludingAdCustomizers({
  text,
  deps,
  adType = 'ExpandedTextAd',
  field = '',
}) {
  const controller = getController(deps, adType);
  return controller.getCharCountExcludingAdCustomizers(text, adType, field);
}

/**
 * Looks for ad customizers in given text and substitutes them with their evaluated values.
 * Used for preview generation.
 * @param {string} text - input text
 * @returns {Object} A promise that resolves to modified input text that contains substitutions
 */
function evaluateAdCustomizers({
  text,
  deps,
  adType = 'ExpandedTextAd',
  field = '',
}) {
  const controller = getController(deps, adType);
  return controller.evaluateAdCustomizers(text, adType, field);
}

export {
  evaluateAdCustomizers,
  getCharCountExcludingAdCustomizers,
  attachAdCustomizersHelpers,
  testResetState,
  checkAdType,
};
